<template>
  <ContentWrap v-loading="formLoading">
    <el-tabs v-model="activeName">
      <el-tab-pane label="基础设置" name="info">
        <InfoForm
          ref="infoRef"
          v-model:activeName="activeName"
          :is-detail="false"
          :propFormData="formData"
        />
      </el-tab-pane>
      <el-tab-pane label="价格库存" name="sku">
        <SkuForm
          ref="skuRef"
          v-model:activeName="activeName"
          :is-detail="false"
          :propFormData="formData"
          mode="merchant"
        />
      </el-tab-pane>
      <el-tab-pane label="物流设置" name="delivery">
        <DeliveryForm
          ref="deliveryRef"
          v-model:activeName="activeName"
          :is-detail="false"
          :propFormData="formData"
          merchant
        />
      </el-tab-pane>

      <el-tab-pane label="商品详情" name="description">
        <DescriptionForm
          ref="descriptionRef"
          v-model:activeName="activeName"
          :is-detail="false"
          :propFormData="formData"
        />
      </el-tab-pane>
      <el-tab-pane label="其它设置" name="other">
        <OtherForm
          ref="otherRef"
          v-model:activeName="activeName"
          :is-detail="false"
          :propFormData="formData"
          merchant
        />
      </el-tab-pane>
    </el-tabs>
    <el-form>
      <el-form-item style="float: right">
        <el-button :loading="formLoading" type="primary" @click="submitForm">保存</el-button>
        <el-button @click="close">返回</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
</template>

<script lang="ts" setup>
import { cloneDeep } from 'lodash-es'
import { useTagsViewStore } from '@/store/modules/tagsView'
import * as MerchantProductApi from '@/api/merchant/product'
import InfoForm from './InfoForm.vue'
import SkuForm from '@/views/mall/product/spu/form/SkuForm.vue'
import DeliveryForm from '@/views/mall/product/spu/form/DeliveryForm.vue'
import DescriptionForm from '@/views/mall/product/spu/form/DescriptionForm.vue'
import OtherForm from '@/views/mall/product/spu/form/OtherForm.vue'
import { convertToInteger, formatToFraction } from '@/utils'
import { isEmpty } from '@/utils/is'
import type { Spu } from '@/api/mall/product/spu'

defineOptions({ name: 'MerchantProductSpuForm' })

const { t } = useI18n()
const message = useMessage()
const { push, currentRoute } = useRouter()
const { query } = useRoute()
const { delView } = useTagsViewStore()

const formLoading = ref(false)
const activeName = ref('info')
const infoRef = ref()
const skuRef = ref()
const deliveryRef = ref()
const descriptionRef = ref()
const otherRef = ref()

const formData = ref<Spu>({
  name: '',
  categoryId: undefined,
  keyword: '',
  picUrl: '',
  sliderPicUrls: [],
  introduction: '',
  deliveryTypes: [],
  deliveryTemplateId: undefined,
  brandId: undefined,
  specType: false,
  subCommissionType: false,
  skus: [
    {
      name: '',
      price: 0,
      marketPrice: undefined as number | undefined,
      costPrice: undefined as number | undefined,
      supplierSettlePrice: undefined as number | undefined,
      barCode: '',
      picUrl: '',
      stock: 0,
      weight: undefined as number | undefined,
      volume: undefined as number | undefined,
      firstBrokeragePercent: 0,
      secondBrokeragePercent: 0
    }
  ],
  description: '',
  sort: 0,
  giveIntegral: 0,
  virtualSalesCount: 0
})

const getDetail = async () => {
  const id = query.id ? Number(query.id) : undefined
  if (id) {
    formLoading.value = true
    try {
      const res = (await MerchantProductApi.getSpu(id)) as Spu
      res.skus?.forEach((item) => {
        item.price = formatToFraction(item.price)
        item.marketPrice =
          item.marketPrice != null ? formatToFraction(item.marketPrice) : undefined
        item.costPrice = item.costPrice != null ? formatToFraction(item.costPrice) : undefined
        item.supplierSettlePrice =
          item.supplierSettlePrice != null ? formatToFraction(item.supplierSettlePrice) : undefined
        item.firstBrokeragePercent = item.firstBrokeragePercent ?? 0
        item.secondBrokeragePercent = item.secondBrokeragePercent ?? 0
      })
      formData.value = res
    } finally {
      formLoading.value = false
    }
  }
}

const submitForm = async () => {
  formLoading.value = true
  try {
    await unref(infoRef)?.validate()
    await unref(skuRef)?.validate()
    await unref(deliveryRef)?.validate()
    await unref(descriptionRef)?.validate()
    const deepCopy = cloneDeep(unref(formData.value)) as Spu
    if (isEmpty(deepCopy.name)) {
      message.error('商品名称不能为空')
      return
    }
    deepCopy.skus!.forEach((item) => {
      item.name = deepCopy.name
      item.price = convertToInteger(item.price)
      item.marketPrice =
        item.marketPrice != null && item.marketPrice !== ''
          ? convertToInteger(item.marketPrice)
          : undefined
      item.costPrice =
        item.costPrice != null && item.costPrice !== ''
          ? convertToInteger(item.costPrice)
          : undefined
      item.supplierSettlePrice =
        item.supplierSettlePrice != null && item.supplierSettlePrice !== ''
          ? convertToInteger(item.supplierSettlePrice)
          : undefined
      item.firstBrokeragePercent = Number(item.firstBrokeragePercent ?? 0)
      item.secondBrokeragePercent = Number(item.secondBrokeragePercent ?? 0)
    })
    const newSliderPicUrls: any[] = []
    deepCopy.sliderPicUrls!.forEach((item: any) => {
      typeof item === 'object' ? newSliderPicUrls.push(item.url) : newSliderPicUrls.push(item)
    })
    deepCopy.sliderPicUrls = newSliderPicUrls
    if (!query.id) {
      await MerchantProductApi.createSpu(deepCopy)
      message.success(t('common.createSuccess'))
    } else {
      await MerchantProductApi.updateSpu(deepCopy)
      message.success(t('common.updateSuccess'))
    }
    close()
  } finally {
    formLoading.value = false
  }
}

const close = () => {
  delView(unref(currentRoute))
  push({ path: '/store/product' })
}

onMounted(async () => {
  await getDetail()
})
</script>
